import { Link, createFileRoute } from "@tanstack/react-router";
import { ChevronRight, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SettingsList, SettingsSection } from "@/components/settings/section";
import { EmptyState, ListSkeleton } from "@/components/shell/page";
import { useShellState } from "@/components/shell/shell-state";
import { useWorkspace } from "@/components/shell/workspace-context";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Tip } from "@/components/ui/tooltip";

export const Route = createFileRoute("/_app/$workspaceSlug/settings/projects/")({
  component: ProjectsSettings,
});

function ProjectsSettings() {
  const { t } = useTranslation("settings");
  const { t: tShell } = useTranslation("shell");
  const { workspace, projects } = useWorkspace();
  const { setCreateProjectOpen } = useShellState();
  const canCreate = workspace.role !== "member";
  const create = canCreate ? (
    <Button variant="outline" size="sm" onClick={() => setCreateProjectOpen(true)}>
      {tShell("commands.newProject")}
    </Button>
  ) : null;

  return (
    <SettingsSection
      title={t("projects.title")}
      description={t("projects.description")}
      actions={projects && projects.length > 0 ? create : null}
    >
      {projects === undefined ? (
        <ListSkeleton rows={3} className="rounded-lg border" />
      ) : projects.length === 0 ? (
        <EmptyState
          className="rounded-lg border md:px-4"
          title={canCreate ? tShell("sidebar.noProjects") : tShell("sidebar.noProjectsMember")}
          action={create}
        />
      ) : (
        <SettingsList>
          {projects.map((project) => (
            <li key={project._id}>
              <Link
                to="/$workspaceSlug/settings/projects/$projectSlug"
                params={{ workspaceSlug: workspace.slug, projectSlug: project.slug }}
                className="flex h-11 items-center gap-3 px-3 transition-colors hover:bg-accent"
              >
                <Avatar name={project.name} image={project.image} shape="square" />
                <span className="truncate font-medium">{project.name}</span>
                <span className="type-label font-mono text-tertiary">{project.key}</span>
                {project.private ? (
                  <Tip label={t("access.privateBadge")}>
                    <Lock
                      role="img"
                      aria-label={t("access.privateBadge")}
                      strokeWidth={1.75}
                      className="size-3.5 shrink-0 text-tertiary"
                    />
                  </Tip>
                ) : null}
                <ChevronRight
                  aria-hidden
                  strokeWidth={1.75}
                  className="ml-auto size-4 shrink-0 text-tertiary"
                />
              </Link>
            </li>
          ))}
        </SettingsList>
      )}
    </SettingsSection>
  );
}
